import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { confirmAbandonReportGeneration } from "../utils/reportGenerationGuard";

/**
 * Blocks in-app navigation while AI report generation or customization is in progress.
 * BrowserRouter does not support useBlocker — intercept link clicks and history instead.
 */
export function useSmeReportSessionNavigationGuard(
  isGeneratingReport: boolean,
  isCustomizingReport: boolean,
  onAbandon: () => void
): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isGeneratingReport && !isCustomizingReport) {
      return undefined;
    }

    const onDocumentClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (
        !href ||
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:") ||
        href.startsWith("blob:") ||
        anchor.hasAttribute("download")
      ) {
        return;
      }

      let url: URL;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin) return;
      if (
        `${url.pathname}${url.search}${url.hash}` ===
        `${window.location.pathname}${window.location.search}${window.location.hash}`
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      void (async () => {
        const shouldLeave = await confirmAbandonReportGeneration("navigate away");
        if (!shouldLeave) return;
        onAbandon();
        navigate(`${url.pathname}${url.search}${url.hash}`);
      })();
    };

    window.history.pushState({ reportGeneration: true }, "", window.location.href);
    const onPopState = () => {
      void (async () => {
        window.history.pushState({ reportGeneration: true }, "", window.location.href);
        const shouldLeave = await confirmAbandonReportGeneration("go back");
        if (!shouldLeave) return;
        onAbandon();
        window.history.go(-2);
      })();
    };

    document.addEventListener("click", onDocumentClick, true);
    window.addEventListener("popstate", onPopState);
    return () => {
      document.removeEventListener("click", onDocumentClick, true);
      window.removeEventListener("popstate", onPopState);
    };
  }, [isGeneratingReport, isCustomizingReport, navigate, onAbandon]);
}
